import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Link2, MoreVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { BenefitRow, CardArt, Eyebrow } from '@/components/perks/PerkVisuals'
import { useBenefitActions } from '@/components/perks/useBenefitActions'
import { usePerkDialogs } from '@/components/perks/usePerkDialogs'
import { perksApi, usePerks, usePerksMutation } from '@/lib/perksApi'
import { capturedThisCardYear, itemFor, money, nextAnniversary, parseDay, shortDay } from '@/lib/perks'

export function PerkCardPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: state, isLoading } = usePerks()
  const perks = usePerkDialogs(state)
  const mutate = usePerksMutation()
  const { markUsed } = useBenefitActions()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const back = (
    <Button variant="ghost" className="-ml-2 w-fit" onClick={() => navigate('/perks')}>
      <ArrowLeft className="mr-2 h-4 w-4" />
      Card Perks
    </Button>
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        {back}
        <Skeleton className="h-64" />
      </div>
    )
  }

  const card = state?.cards.find((c) => String(c.id) === id)
  if (!state || !card) {
    return (
      <div className="space-y-6">
        {back}
        <Card>
          <CardContent className="py-12 text-center text-sm text-gray-500">This card may have been removed.</CardContent>
        </Card>
      </div>
    )
  }

  const today = new Date()
  const captured = capturedThisCardYear(card, today)
  const renews = nextAnniversary(parseDay(card.opened_on), today)
  const gap = card.annual_fee_cents - captured
  const fraction = card.annual_fee_cents > 0 ? captured / card.annual_fee_cents : 0
  const account = state.credit_accounts.find((a) => a.account_id === card.account_id)
  const items = card.benefits
    .map((b) => itemFor(state, card, b, today))
    .sort((a, b) =>
      a.status.resolved !== b.status.resolved
        ? Number(a.status.resolved) - Number(b.status.resolved)
        : a.status.period.end.getTime() - b.status.period.end.getTime() || a.benefit.sort_index - b.benefit.sort_index
    )

  const remove = async () => {
    setDeleting(true)
    try {
      await mutate(() => perksApi.deleteCard(card.id))
      toast.success(`${card.name} removed`)
      navigate('/perks')
    } catch {
      toast.error('Unable to remove card')
      setDeleting(false)
      setConfirmDelete(false)
    }
  }

  return (
    <div className="space-y-6">
      {back}
      <div className="grid gap-6 md:grid-cols-[minmax(0,380px)_1fr]">
        <div className="space-y-4">
          <CardArt art={card.art} issuer={card.issuer} name={card.name} mask={card.mask} />

          <Card>
            <CardContent className="space-y-4 p-5">
              <Eyebrow>This card year</Eyebrow>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Back" value={money(captured)} accent={captured > 0} />
                <Stat label="Annual fee" value={money(card.annual_fee_cents)} />
                <Stat label="Renews" value={shortDay(renews)} />
              </div>
              {card.annual_fee_cents > 0 && (
                <>
                  <div className="h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                    <div className={fraction >= 1 ? 'h-full bg-success' : 'h-full bg-gray-500'} style={{ width: `${Math.max(Math.min(fraction, 1) * 100, 0.5)}%` }} />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {gap > 0 ? `${money(gap)} more to cover the fee before ${shortDay(renews)}.` : `Paid for itself, ${money(-gap)} ahead this card year.`}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-start gap-3 p-5 text-sm text-gray-600 dark:text-gray-400">
              <Link2 className="mt-0.5 h-4 w-4 shrink-0" />
              {card.account_id ? (
                <span>
                  Linked to {account?.name ?? 'your card account'}
                  {card.mask ? ` •• ${card.mask}` : ''}. Statement credits are picked up automatically.
                </span>
              ) : (
                <span>
                  Not linked to an account.{' '}
                  {state.credit_accounts.some((a) => a.card_id === null) && (
                    <button type="button" className="font-medium text-primary-600 hover:underline" onClick={() => perks.editCard(card)}>
                      Link it
                    </button>
                  )}{' '}
                  to pick up statement credits automatically.
                </span>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="self-start">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>Benefits <span className="ml-1 text-sm font-normal text-gray-500">{card.benefits.length}</span></CardTitle>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => perks.addBenefit(card)}>
                <Plus className="mr-1 h-4 w-4" /> Add benefit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Card options">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => perks.editCard(card)}>
                    <Pencil className="mr-2 h-4 w-4" /> Edit card
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-danger" onClick={() => setConfirmDelete(true)}>
                    <Trash2 className="mr-2 h-4 w-4" /> Remove card
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {items.length === 0 && <p className="py-6 text-sm text-gray-500">No benefits yet. Add the credits this card gives you.</p>}
            <div className="-mx-3">
              {items.map((item) => (
                <BenefitRow key={item.benefit.id} item={item} showCard={false} onOpen={() => perks.openBenefit(item.benefit.id)} onQuickUse={() => markUsed(item)} />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Remove ${card.name}?`}
        description="Its benefits and logged uses are deleted. Your bank account and transactions stay as they are."
        confirmLabel="Remove card"
        destructive
        isConfirming={deleting}
        onConfirm={remove}
      />
      {perks.dialogs}
    </div>
  )
}

function Stat({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className={accent ? 'mt-0.5 font-semibold tabular-nums text-success' : 'mt-0.5 font-semibold tabular-nums text-gray-900 dark:text-gray-100'}>{value}</div>
    </div>
  )
}
